/**
 * Shared primitives for the command-buffer slice modules.
 *
 * Every sibling module in this folder imports from here — scratch vectors,
 * setter/getter aliases, and helpers that don't belong to any single command
 * type. Keeping them in one place avoids per-frame allocations scattered
 * across files and makes the sharing contract explicit.
 *
 * Scratch vector discipline: only one command is ever active at a time, so
 * the scratches are safe to share across sibling modules. Do NOT rely on
 * scratch state persisting across a function-call boundary — treat each
 * function as if it can clobber any of the `_scratch*` slots.
 */

import * as THREE from "three/webgpu";
import type { ScalarParamDef } from "../../modeling/operatorParams.ts";
import type {
  AppState,
  CommandBuffer,
  CommandSnapshot,
} from "../storeTypes.ts";
import { visibleHeight } from "./transformMath.ts";

export type Set_ = (partial: Partial<AppState>) => void;
export type Get_ = () => AppState;

// ---------------------------------------------------------------------------
// Reusable scratch objects — avoids per-frame allocations in applyCommandLive
// and its split helper modules. These are shared across ALL command modules;
// each call site must assume any of them can be overwritten by a nested call.
// ---------------------------------------------------------------------------
export const _scratchVec3A = new THREE.Vector3();
export const _scratchVec3B = new THREE.Vector3();
export const _scratchVec3C = new THREE.Vector3();
export const _scratchVec3D = new THREE.Vector3();
export const _scratchVec2A = new THREE.Vector2();
export const _scratchMat4 = new THREE.Matrix4();
export const _scratchMat4B = new THREE.Matrix4();
export const _scratchQuat = new THREE.Quaternion();
export const _scratchEuler = new THREE.Euler();

// Dedicated scratches for resolveKeyboardAxis so it never aliases the
// per-frame apply scratches above.
export const _kbQuat = new THREE.Quaternion();
export const _kbEuler = new THREE.Euler();
export const _kbVec = new THREE.Vector3();

// ---------------------------------------------------------------------------
// Live-edit vertex scratch — double-buffered Float32Array pair reused
// across pointermove frames during a single active command.
//
// Eliminates the 12 MB-per-frame Float32Array allocation (and resulting
// GC stalls) that used to dominate live drag on 1M-vertex meshes. Why two
// buffers rather than one: React's `useEffect` identity check on
// `[obj.vertices, ...]` (see PrimitiveRenderer's in-place position sync)
// skips the effect when the Float32Array reference is unchanged — so a
// single-buffer scratch would silently freeze the GPU position stream.
// Alternating between two buffers gives every frame a distinct identity
// while keeping the total allocation count at two per command.
//
// Safe because:
//   1. Pointer-driven applyCommandLive dispatch is RAF-coalesced before it
//      reaches the store, so only one write happens per animation frame.
//   2. React commit + in-place GPU upload run synchronously within that
//      frame tick — by the time the next RAF rewrites the *other* buffer,
//      the previous frame's positions have already been shipped to the GPU.
//   3. Only one command is active at a time; lengths track the active
//      snapshot's vertex count and realloc only when it changes.
// ---------------------------------------------------------------------------
let _liveVertsScratchA: Float32Array | null = null;
let _liveVertsScratchB: Float32Array | null = null;
let _liveVertsScratchToggle = false;

/**
 * Acquire a reusable Float32Array of the given length, suitable for
 * writing per-frame live-edit vertex positions. Alternates between two
 * internal buffers so consecutive calls return DIFFERENT references — the
 * caller can safely hand the result to React without masking a change.
 */
export function acquireLiveVertsScratch(length: number): Float32Array {
  _liveVertsScratchToggle = !_liveVertsScratchToggle;
  if (_liveVertsScratchToggle) {
    if (_liveVertsScratchA === null || _liveVertsScratchA.length !== length) {
      _liveVertsScratchA = new Float32Array(length);
    }
    return _liveVertsScratchA;
  }
  if (_liveVertsScratchB === null || _liveVertsScratchB.length !== length) {
    _liveVertsScratchB = new Float32Array(length);
  }
  return _liveVertsScratchB;
}

/**
 * Drop both live-verts scratches so the next command allocates fresh.
 * Called on command end (confirm/cancel) so committed geometry never
 * retains references into the reusable scratch pool.
 */
export function releaseLiveVertsScratch(): void {
  _liveVertsScratchA = null;
  _liveVertsScratchB = null;
  _liveVertsScratchToggle = false;
}

// ---------------------------------------------------------------------------
// ApplyLiveContext — the ambient per-frame state that every applyCommandLive
// branch needs. Built once at the top of applyCommandLive and handed to each
// sub-dispatcher (edit / bone / object). One object allocation per mouse
// move, not per command type, so GC pressure stays flat.
// ---------------------------------------------------------------------------
export interface ApplyLiveContext {
  set: Set_;
  get: Get_;
  state: AppState;
  cb: CommandBuffer;
  snap: CommandSnapshot;
  camera: THREE.Camera;
  rect: DOMRect;
  screenX: number;
  screenY: number;
  /** Post-precision-scale pixel delta from the drag origin. */
  dx: number;
  dy: number;
  /** Drag origin in screen-space CSS pixels. */
  ox: number;
  oy: number;
  snapGridLevelOffset: number;
}

// ---------------------------------------------------------------------------
// mapParamInput — centralized mouse → param-value mapping for topology ops.
//
// Convert a mouse pixel delta into a parameter value using the ParamDef's
// input mapping. Handles both world-space (camera-distance-aware, like
// shrink/fatten) and normalized (viewport-fraction) mappings.
//
// NOTE: dx/dy are expected to already have precision scaling applied via
// getInputScale().
// ---------------------------------------------------------------------------
export function mapParamInput(
  paramDef: ScalarParamDef,
  dx: number,
  dy: number,
  rect: DOMRect,
  camera: THREE.Camera,
  objMatrix: THREE.Matrix4,
  px: number,
  py: number,
  pz: number,
): number {
  if (paramDef.inputMapping === "worldSpace") {
    const pivotWorld = _scratchVec3C.set(px, py, pz).applyMatrix4(objMatrix);
    const dist = camera.position.distanceTo(pivotWorld);
    return (dx * visibleHeight(camera, dist)) / rect.height;
  }
  if (paramDef.inputMapping === "radial") {
    // Any drag direction increases value (used by inset thickness)
    const factor = paramDef.viewportFactor ?? 0.4;
    return Math.sqrt(dx * dx + dy * dy) / (rect.width * factor);
  }
  // normalized
  const factor = paramDef.viewportFactor ?? 0.4;
  return dx / (rect.width * factor);
}
